/**
 * Type de bien Skyline → classification Immoweb (estateType + estateSubType).
 *
 * Décision D#2 : un seul déroulant côté back office pour la fiche LOCATION
 * (100% appartements/penthouses). On en dérive automatiquement :
 *   estateType    = "APARTMENT"
 *   estateSubType = { apartment: <sous-type> }
 *
 * La VALEUR stockée dans `biens.typeBien` est une **clé neutre** (= l'enum
 * Immoweb `estateSubType.apartment`), affichée via un libellé i18n. Les
 * helpers tolèrent aussi les anciennes valeurs FR (avant la migration 082)
 * pour rester robustes pendant/après la bascule.
 *
 * La fiche VENTE (`typesBienVente` dans venteFormFields.ts) propose EN PLUS
 * des maisons (HOUSE/VILLA/BUNGALOW/MANSION/FARMHOUSE/TOWN_HOUSE/CASTLE/
 * CHALET) — ces 8 valeurs sont déjà littéralement les clés de l'enum Immoweb
 * `estateSubType.house`. Un bien vente avec un de ces `typeBien` était
 * jusqu'ici publié comme un APPARTEMENT par erreur (`deriveEstate` ignorait
 * la famille maison) — trouvé en auditant le mapping vente en entier le
 * 2026-09-12. Réf. schéma : docs/immoweb/classifieds-pipeline-3.openapi.json.
 */

import type { TFunction } from "i18next";

/** Sous-types d'appartement (valeur = enum Immoweb `estateSubType.apartment`). */
export const APARTMENT_SUBTYPES = [
  "APARTMENT",
  "PENTHOUSE",
  "STUDIO",
  "DUPLEX",
  "TRIPLEX",
  "LOFT",
  "GROUND_FLOOR",
  "KOT",
] as const;
export type ApartmentSubType = (typeof APARTMENT_SUBTYPES)[number];

/** Sous-types de maison utilisés côté vente (`typesBienVente`) — sous-ensemble
 *  de l'enum Immoweb `estateSubType.house` (qui en compte 14 au total). */
export const HOUSE_SUBTYPES = [
  "HOUSE",
  "VILLA",
  "BUNGALOW",
  "MANSION",
  "FARMHOUSE",
  "TOWN_HOUSE",
  "CASTLE",
  "CHALET",
] as const;
export type HouseSubType = (typeof HOUSE_SUBTYPES)[number];

/** Anciennes valeurs FR (avant migration) → clé neutre. */
const LEGACY_FR: Record<string, ApartmentSubType> = {
  Appartement: "APARTMENT",
  Penthouse: "PENTHOUSE",
  Studio: "STUDIO",
  Duplex: "DUPLEX",
  Triplex: "TRIPLEX",
  Loft: "LOFT",
  "Rez-de-chaussée": "GROUND_FLOOR",
};

/** Clé i18n par sous-type (réutilise les libellés `options.typesBien.*`). */
const LABEL_KEY: Record<ApartmentSubType, string> = {
  APARTMENT: "options.typesBien.appartement",
  PENTHOUSE: "options.typesBien.penthouse",
  STUDIO: "options.typesBien.studio",
  DUPLEX: "options.typesBien.duplex",
  TRIPLEX: "options.typesBien.triplex",
  LOFT: "options.typesBien.loft",
  GROUND_FLOOR: "options.typesBien.rdc",
  KOT: "options.typesBien.kot",
};

/** Normalise une valeur `typeBien` (clé neutre OU ancienne valeur FR) en sous-type. */
export function normalizeTypeBien(typeBien: string | null | undefined): ApartmentSubType | null {
  if (!typeBien) return null;
  const v = typeBien.trim();
  if ((APARTMENT_SUBTYPES as readonly string[]).includes(v)) return v as ApartmentSubType;
  return LEGACY_FR[v] ?? null;
}

/** Normalise une valeur `typeBien` en sous-type maison, si c'en est une. */
export function normalizeHouseType(typeBien: string | null | undefined): HouseSubType | null {
  if (!typeBien) return null;
  const v = typeBien.trim();
  return (HOUSE_SUBTYPES as readonly string[]).includes(v) ? (v as HouseSubType) : null;
}

/** Libellé i18n d'une valeur `typeBien`. Fallback = valeur brute (jamais vide cassé). */
export function typeBienLabel(typeBien: string | null | undefined, t: TFunction): string {
  const st = normalizeTypeBien(typeBien);
  return st ? t(LABEL_KEY[st]) : (typeBien || "");
}

export type EstateClassification =
  | { estateType: "APARTMENT"; estateSubType: { apartment: ApartmentSubType } }
  | { estateType: "HOUSE"; estateSubType: { house: HouseSubType } };

/**
 * Dérive `estateType` + `estateSubType` Immoweb depuis `typeBien`.
 * Une valeur maison (fiche vente) → `HOUSE`. Sinon → `APARTMENT`, avec
 * défaut `APARTMENT` si la valeur est inconnue/vide (cf. mapping doc, gap #2).
 */
export function deriveEstate(typeBien: string | null | undefined): EstateClassification {
  const house = normalizeHouseType(typeBien);
  if (house) return { estateType: "HOUSE", estateSubType: { house } };
  const sub = normalizeTypeBien(typeBien) ?? "APARTMENT";
  return { estateType: "APARTMENT", estateSubType: { apartment: sub } };
}
